"use client";

import { generateSocialBio } from "@/actions/free-tools";
import Alert, { AlertType } from "@/components/Alerts";
import ComboBox from "@/components/ui/combobox";
import languages from "@/data/languages.json";
import toneList from "@/data/tone.json";
import { zodResolver } from "@hookform/resolvers/zod";
import { BioGeneratorFormSchema, IBioGeneratorFormInput } from "@listing/llm";
import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AIToolsResponse from "@/components/AIToolsResponse";

type Props = {
  type: string;
  appName: string;
};

type ComboBoxSelection = {
  value: string;
  id: string;
};

const defaultTone = toneList?.find((tone) => tone.id === "professional");
const defaultLanguage = languages?.find((language) => language.id === "English");

const BioGenerator: React.FC<Props> = ({ type, appName }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<IBioGeneratorFormInput>({
    resolver: zodResolver(BioGeneratorFormSchema),
    defaultValues: {
      tone: defaultTone,
      language: defaultLanguage,
    },
  });

  const [response, setResponse] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<Boolean>(false);
  const onSelectTone = (selectedItem: ComboBoxSelection) => {
    setValue("tone", selectedItem);
  };

  const onSelectLanguage = (selectedItem: ComboBoxSelection) => {
    setValue("language", selectedItem);
  };

  const onRegenerate = async () => {
    await generateSocialBioForm(getValues(), true);
  };

  async function generateSocialBioForm(input: IBioGeneratorFormInput, hasRegenerateRequest = false) {
    console.log({
      input,
    });
    setIsSubmitting(true);
    try {
      const result = await generateSocialBio({
        info: input,
        platform: type,
        old: hasRegenerateRequest ? response : "",
      });
      if (!result) {
        // Sentry error
        toast.custom(<Alert type={AlertType.Error} content="Something went wrong" />);
        return;
      }

      if (result.error) {
        // @ts-ignore
        const error = result.error?._errors ? result.error?._errors?.join(", ") : result.error?.message;
        toast.custom(<Alert type={AlertType.Error} content={error} />);
        return;
      }

      if (result.data) {
        setResponse(result.data);
        return;
      }

      toast.custom(
        <Alert type={AlertType.Success} content={"We are processing your request. Please check after sometime"} />
      );
    } catch (e) {
      // Sentry error
      toast.custom(<Alert type={AlertType.Error} content="Something went wrong" />);
    } finally {
      setIsSubmitting(false);
    }
  }

  const onSubmit: SubmitHandler<IBioGeneratorFormInput> = async (input) => {
    await generateSocialBioForm(input);
    return false;
  };

  return (
    <div className="flex flex-col space-x-1 p-2 sm:flex-row sm:items-stretch">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="form-control flex flex-1 space-y-4 rounded-lg bg-black p-2 text-white sm:border-2 sm:border-gray-600 sm:p-3 "
      >
        <div className="bg-blend-overlay"></div>
        <section className="card bg-base-200/70 mx-auto  flex h-full min-h-[300px] w-full justify-start rounded-lg px-4 py-4 align-middle">
          <h1 className="mb-5 w-full text-center text-3xl sm:mb-10">{`Free AI ${appName} Bio Generator`}</h1>
          <div className="form-control mb-4 flex w-full flex-row items-center">
            <label className="label mr-5 min-w-fit sm:min-w-[90px]" htmlFor="tone-select">
              <span className="label-text text-base font-semibold">Tone</span>
            </label>
            <ComboBox
              {...register("tone")}
              items={toneList}
              itemToString={(item: any) => item.value}
              defaultValue={defaultTone}
              onSelect={onSelectTone}
            />
          </div>
          <div className="form-control mb-4 flex w-full flex-row items-center">
            <label className="label mr-5 min-w-fit sm:min-w-[90px]" htmlFor="language-select">
              <span className="label-text text-base font-semibold">Language</span>
            </label>
            <ComboBox
              {...register("language")}
              items={languages}
              itemToString={(item: any) => item.value}
              defaultValue={defaultLanguage}
              onSelect={onSelectLanguage}
            />
          </div>
          <div className="mt-4">
            <label htmlFor="website" className="label-text ml-2 text-base font-semibold">
              Enter your existing bio or simply provide a few key terms
            </label>
            <textarea
              {...register("content")}
              className="textarea textarea-bordered mr-2 mt-4 h-40 w-full p-4 text-base"
              placeholder="Example: Founder & CEO of DCoderAI with over two decades of pioneering in the technology sphere."
            />
            {errors.content && <p className="mt-2 text-sm text-red-600">{errors.content.message}</p>}
          </div>
          <div className="mt-5 flex flex-col items-center justify-start gap-4 sm:mt-2 sm:flex-row">
            <div className="form-control mr-5">
              <label className="label cursor-pointer">
                <input {...register("addEmojis")} type="checkbox" className="toggle" />
                <span className="label-text ml-2 text-base font-semibold">Add 😎 Emojis</span>
              </label>
            </div>
            <div className="form-control">
              <label className="label cursor-pointer">
                <input {...register("addHashtags")} type="checkbox" className="toggle" />
                <span className="label-text ml-2 text-base font-semibold">Add # Hashtags</span>
              </label>
            </div>
          </div>
        </section>
        <button type="submit" className="btn btn-primary" disabled={Boolean(isSubmitting)}>
          <svg className="" height="24px" width="24px" viewBox="0 0 16 16">
            <path
              className="css-kqzqgg"
              d="M0.666656 4.34121V3.69182L1.01511 3.66015C2.37724 3.53344 3.29589 2.5356 3.43843 0.999239L3.47011 0.666626H4.18285L4.21453 0.999239C4.35708 2.5356 5.27573 3.53344 6.6537 3.66015L6.98631 3.69182V4.34121L6.6537 4.35705C5.32324 4.42041 4.37292 5.44992 4.21453 7.0338L4.18285 7.36641H3.47011L3.43843 7.0338C3.28005 5.44992 2.34556 4.42041 1.01511 4.35705L0.666656 4.34121ZM5.13318 9.53632V8.76022L7.76241 8.15835C8.80776 7.92077 8.99783 7.63567 9.17205 6.55863L9.72641 3.12163H10.7401L11.2944 6.55863C11.4687 7.63567 11.6587 7.92077 12.6883 8.15835L15.3333 8.76022V9.53632L12.6883 10.154C11.6587 10.3916 11.5003 10.6767 11.2944 11.7537L10.7401 15.3333H9.72641L9.17205 11.7537C8.99783 10.6767 8.80776 10.3916 7.76241 10.154L5.13318 9.53632ZM1.14182 11.7379V11.4686C2.04463 11.1202 2.58314 10.5658 2.97911 9.53632H3.18501C3.59682 10.5658 4.15118 11.136 5.03815 11.4686V11.7379C4.15118 12.0864 3.58098 12.5615 3.18501 13.7019H2.97911C2.58314 12.5615 2.04463 12.0864 1.14182 11.7379Z"
              fill-rule="evenodd"
            ></path>
          </svg>
          Start Making Your Perfect Bio Now
        </button>
      </form>
      <div className="h-full px-2" />
      <AIToolsResponse onRegenerate={onRegenerate} response={response} />
    </div>
  );
};

export default BioGenerator;
